import {Box} from '@mui/material';
import {EvStationOutlined} from '@mui/icons-material';
import {usePersona} from 'src/permissions/hooks';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {chargersApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {ChargerTable} from 'src/components/chargers/ChargerTable';
import {ChargerSortFieldEnum} from 'src/enums';
import {store} from 'src/app/store';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ChargePointSearchRequestData, ListParamsType} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {sectionSx} from './LocationChargersList.sx';

interface LocationChargersListProps {
    title: string;
    locationId: number;
    businessId: number;
}

export const LocationChargersList: FC<LocationChargersListProps> = ({title, locationId, businessId}) => {
    const defaultPageSize = 100;
    const {authInfo} = useAppSelector((state) => state.common);
    const persona = usePersona();
    const isCompanyUser = persona.isCompany;
    const companyId = authInfo?.company?.id || authInfo?.employee?.company?.id;

    const [trigger, response] = chargersApi.useLazyGetChargersQuery();
    const [tableParams, setTableParams] = useState<ListParamsType>({
        page: 0,
        size: defaultPageSize,
    });
    const [filterParams, setFilterParams] = useState<ChargePointSearchRequestData>({
        locationId,
        sortField: ChargerSortFieldEnum.CREATED_AT,
        sortAsc: false,
    });

    const callHandler = async (data: ChargePointSearchRequestData, params: ListParamsType) => {
        store.dispatch(preloaderChangeStatus(true));
        try {
            await trigger({
                data: {
                    ...data,
                    businessId,
                    locationId,
                    ...(companyId ? {companyId} : {}),
                },
                params,
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    };

    const handleChange = (value: ChargePointSearchRequestData) => {
        const newParams = {...filterParams, ...value};
        setFilterParams(newParams);
        setTableParams({page: 0, size: tableParams.size});
        void callHandler(newParams, {page: 0, size: tableParams.size});
    };

    const handleChangePage = (page: number) => {
        setTableParams({page, size: tableParams.size});
        void callHandler(filterParams, {page, size: tableParams.size});
    };

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const size = +e.target.value || defaultPageSize;
        setTableParams({page: 0, size});
        void callHandler(filterParams, {page: 0, size});
    };

    useEffect(() => {
        void callHandler(filterParams, tableParams);
    }, []);

    return (
        // No card of its own: ChargerTable brings a TableLayout, which is already a card. Wrapping it
        // put one white rectangle inside another - the table looked like it had been dropped onto a
        // sheet of paper that nothing else on the page had.
        <Box sx={sectionSx}>
            {response.data && (
                <ChargerTable
                    // The heading belongs to the table, as it does on the list screens: outside the
                    // card it sat apart from the row count and the badge that say which list it is.
                    title={title}
                    icon={<EvStationOutlined/>}
                    onlyTitle
                    data={response.data}
                    filters={filterParams}
                    pageSize={tableParams.size}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    isCompanyUser={isCompanyUser}
                />
            )}
        </Box>
    );
};